import { useEffect, useState } from 'react';
import { CURRENCY_CODES } from '../utils/currency';

const CACHE_KEY = 'fx-rates-v1';
const CACHE_TTL = 6 * 60 * 60 * 1000;

// Rates per 1 USD. Only reached when every live source is unreachable, so these
// exist to keep the pricing grid readable offline, not to be accurate forever.
const FALLBACK_RATES = { USD: 1, PKR: 278.23, EUR: 0.876, GBP: 0.748 };

// Both are keyless and send `Access-Control-Allow-Origin: *`, which a static
// frontend needs — an API key here would ship to the browser anyway.
const SOURCES = [
	{
		url: 'https://open.er-api.com/v6/latest/USD',
		read: (data) =>
			data?.result === 'success'
				? { rates: data.rates, updatedAt: data.time_last_update_unix * 1000 }
				: null,
	},
	{
		url: 'https://api.exchangerate-api.com/v4/latest/USD',
		read: (data) => (data?.rates ? { rates: data.rates, updatedAt: Date.parse(data.date) } : null),
	},
];

/** Narrows a full rate table to the currencies we offer, or null if any is missing. */
const pickRates = (rates) => {
	const picked = {};

	for (const code of CURRENCY_CODES) {
		if (!Number.isFinite(rates?.[code])) return null;
		picked[code] = rates[code];
	}

	return picked;
};

// Storage throws in Safari private mode, and a broken cache read shouldn't
// take the section down with it.
const readCache = () => {
	try {
		const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
		if (!cached || Date.now() - cached.fetchedAt > CACHE_TTL) return null;

		return pickRates(cached.rates) ? { rates: cached.rates, updatedAt: cached.updatedAt } : null;
	} catch {
		return null;
	}
};

const writeCache = (payload) => {
	try {
		sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...payload, fetchedAt: Date.now() }));
	} catch {
		/* cache is an optimisation, never a requirement */
	}
};

/**
 * @returns {{ rates: Record<string, number>, updatedAt: number|null, status: 'loading'|'live'|'fallback' }}
 */
export const useExchangeRates = () => {
	const [state, setState] = useState(() => {
		const cached = readCache();

		return cached
			? { ...cached, status: 'live' }
			: { rates: FALLBACK_RATES, updatedAt: null, status: 'loading' };
	});

	useEffect(() => {
		if (readCache()) return undefined;

		const controller = new AbortController();
		let cancelled = false;

		(async () => {
			for (const source of SOURCES) {
				try {
					const response = await fetch(source.url, { signal: controller.signal });
					if (!response.ok) continue;

					const parsed = source.read(await response.json());
					const rates = parsed && pickRates(parsed.rates);
					if (!rates) continue;

					const next = {
						rates,
						updatedAt: Number.isFinite(parsed.updatedAt) ? parsed.updatedAt : Date.now(),
					};

					writeCache(next);
					if (!cancelled) setState({ ...next, status: 'live' });
					return;
				} catch {
					if (controller.signal.aborted) return;
				}
			}

			if (!cancelled) setState({ rates: FALLBACK_RATES, updatedAt: null, status: 'fallback' });
		})();

		return () => {
			cancelled = true;
			controller.abort();
		};
	}, []);

	return state;
};
